/**
 * Finding the row behind a PARKED call's offer while the phone rings.
 *
 * The server can honestly answer "not yet" for a moment: Twilio may deliver the
 * offer to this browser before the REST call that created it has returned to
 * the server, and the leg sids are written down only then. So the card asks a
 * few times on a short, growing interval, and stops the moment the offer is no
 * longer this card's.
 *
 * React-free and handed its request rather than importing one, so the schedule
 * can be tested without a component and without a network.
 */
import type { ParkedOffer } from "../../lib/calls";

/**
 * Waits before each attempt, in milliseconds. About eight seconds in all: well
 * inside the desk's ring time, and far past the second or two the server needs
 * to write the legs down.
 */
export const PARKED_OFFER_DELAYS_MS: readonly number[] = [0, 250, 500, 750, 1000, 1500, 2000, 2000];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The offer's row, or null when it never turned up or the offer went away.
 *
 * Every failure is worth another try while the phone is ringing - a 404 is the
 * ordinary "not yet", and anything else is more likely a blip than a verdict.
 * Null is not an error for the caller either: the call can still be answered,
 * it just goes without the department, the language and the claim.
 */
export async function waitForParkedOffer(
  legSid: string,
  live: () => boolean,
  find: (legSid: string) => Promise<ParkedOffer>,
  opts: { delays?: readonly number[]; sleep?: (ms: number) => Promise<void> } = {},
): Promise<ParkedOffer | null> {
  const delays = opts.delays ?? PARKED_OFFER_DELAYS_MS;
  const sleep = opts.sleep ?? wait;
  for (const delay of delays) {
    if (delay > 0) await sleep(delay);
    if (!live()) return null;
    try {
      return await find(legSid);
    } catch {
      // Not yet, or a blip. Asked again on the next step.
    }
  }
  return null;
}
